import { ComponentUsageError } from 'BomdisoftComponents/errors/index';
import { type AttributeValue, attributes, type HtmlValue, html, joinClasses } from 'BomdisoftComponents/html/index';
import { renderTabOverflowArrows, renderTabOverflowFrame, type TabOverflowDirection, type TabOverflowLabels } from 'BomdisoftComponents/tab/render';

export type ToggleGroupSize = 'medium' | 'small';
export type ToggleGroupActiveVariant = 'neutral' | 'primary' | 'success' | 'error';

/**
 * `joined` is the shared segmented control: one border around the whole control,
 * segments owning no edges, selection a tint of the active tone. `pill` is the
 * full-radius track with a paper-filled active segment, per the clepit console's
 * approved comments filter (design record: the clepit design artifact, document
 * state 2).
 */
export type ToggleGroupShape = 'joined' | 'pill';

/**
 * What the control does when its words exceed its box, which every consumer
 * that puts a group in a narrow or resizable container hits (design record:
 * `docs/design/segmented-overflow-options.html`, picked 2026-09-08).
 *
 * `wrap` is the default because nothing is ever hidden by it: the control grows
 * downward and every option stays reachable with no gesture. `scroll` is for a
 * placement that cannot afford a control whose height changes with the locale,
 * and it wears the platform's overflow chrome so the row admits it has more.
 */
export type ToggleGroupOverflow = 'scroll' | 'wrap';

export type ToggleGroupOptions = {
  activeVariant?: ToggleGroupActiveVariant;
  attributes?: Readonly<Record<string, AttributeValue>>;
  children?: HtmlValue;
  className?: string;
  label?: HtmlValue;
  overflow?: ToggleGroupOverflow;
  scrollLabels?: TabOverflowLabels;
  shape?: ToggleGroupShape;
  tooltipIdOf?: (direction: TabOverflowDirection) => string;
};

export type ToggleButtonOptions = {
  activeVariant?: ToggleGroupActiveVariant;
  attributes?: Readonly<Record<string, AttributeValue>>;
  children?: HtmlValue;
  className?: string;
  description?: HtmlValue;
  isPressed?: boolean;
  shape?: ToggleGroupShape;
  size?: ToggleGroupSize;
  value: string;
};

export const renderToggleGroup = ({ activeVariant = 'primary', attributes: extra = {}, children, className, label, overflow = 'wrap', scrollLabels, shape = 'joined', tooltipIdOf }: ToggleGroupOptions) => {
  const scrolling = overflow === 'scroll';
  const groupClassName = joinClasses('bomdisoft-segmented', shape === 'pill' && 'bomdisoft-toggle-group-pill', scrolling && 'bomdisoft-tab-overflow-scroller', label ? undefined : className);
  const state = { 'data-overflow': overflow, 'data-shape': shape, 'data-tone': activeVariant };
  const arrows = () => {
    if (!scrollLabels || !tooltipIdOf) throw new ComponentUsageError('missing_option', 'renderToggleGroup needs scrollLabels and tooltipIdOf to draw a scrolling group');
    return renderTabOverflowArrows({ labels: scrollLabels, tooltipIdOf });
  };
  const control = html`<div${attributes({ class: groupClassName, ...state })}>${children}</div>`;
  if (!label) {
    if (!scrolling) return html`<fieldset${attributes({ ...extra, class: groupClassName, ...state })}>${children}</fieldset>`;
    return html`<fieldset${attributes({ ...extra, class: joinClasses('bomdisoft-tab-overflow', 'bomdisoft-toggle-group-scroll-frame', className) })}>${control}${arrows()}</fieldset>`;
  }
  return html`<fieldset${attributes({ ...extra, class: joinClasses('bomdisoft-toggle-group-field', className) })}><legend class="bomdisoft-toggle-group-legend">${label}</legend>${scrolling ? renderTabOverflowFrame({ children: html`${control}${arrows()}` }) : control}</fieldset>`;
};

export const renderToggleButton = ({ activeVariant, attributes: extra = {}, children, className, description, isPressed = false, shape = 'joined', size = 'medium', value }: ToggleButtonOptions) =>
  html`<button${attributes({
    ...extra,
    'aria-pressed': isPressed ? 'true' : 'false',
    class: joinClasses('bomdisoft-segmented-segment', shape === 'pill' && 'bomdisoft-toggle-group-pill-segment', description !== undefined && 'bomdisoft-toggle-group-described', className),
    'data-selected': isPressed,
    'data-shape': shape,
    'data-size': size,
    'data-tone': activeVariant,
    type: 'button',
    value,
  })}>${description === undefined ? children : html`<span class="bomdisoft-toggle-group-name">${children}</span>`}${description === undefined ? '' : html`<span class="bomdisoft-toggle-group-description">${description}</span>`}</button>`;

export const resolveExclusiveValue = (selected: string | null, clicked: string): string | null => (selected === clicked ? null : clicked);
